import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../lib/api"
import { getCurrentUser } from "../../lib/auth"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import { TempPasswordReveal } from "../../components/admin/TempPasswordReveal"
import { TourButton } from "../../components/admin/TourButton"
import { supportAccountsTourSteps } from "../../lib/tours/supportAccountsTour"
import Badge from "../../components/ui/badge/Badge"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { PendingMemberReconcileRow, type PendingMember } from "../../components/admin/PendingMemberReconcileRow"
import { studentsAdmin } from "../../lib/api/studentsAdminClient"
import { applyOptimistic, rollbackOptimistic } from "../../lib/optimisticMutation"
import { showUndoToast } from "../../lib/undoToast"
import { toastError, toastSuccess } from "../../lib/toast"
import { EyeIcon, LockIcon, CheckCircleIcon, CloseIcon, SproutIcon } from "../../icons"
import { completionPercent, completionTone, COMPLETION_TONE_CLASS, type StudentProfileFields } from "../../components/admin/StudentProfileSections"

type Student = {
  id: string
  name: string
  email: string
  role: string
  createdAt: string
  isActive: boolean
  mustChangePassword: boolean
  studentProfile: StudentProfileFields | null
}

interface StudentsPayload {
  users: Student[]
  pagination: { total: number }
}

type StudentStats = {
  total: number
  active: number
  blocked: number
  newLast30Days: number
  gender: { male: number; female: number; nonBinary: number; undeclared: number }
}

export default function StudentsManager() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [stats, setStats] = useState<StudentStats | null>(null)
  const [search, setSearch] = useState("")
  const [searchParams] = useSearchParams()
  // /instructor/students?filter=at-risk (enlace del Panel del instructor) abre la lista ya filtrada.
  const [atRiskOnly, setAtRiskOnly] = useState(searchParams.get("filter") === "at-risk")
  const [selectedPending, setSelectedPending] = useState<PendingMember | null>(null)
  const [reveal, setReveal] = useState<{ label: string; value: string } | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [deleting, setDeleting] = useState(false)
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false)
  const closeDrawerButtonRef = useRef<HTMLButtonElement>(null)

  const isSuperAdmin = getCurrentUser()?.role === "admin"

  const [pendingResults, setPendingResults] = useState<PendingMember[]>([])

  const {
    rows: students,
    total: studentsTotal,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error,
    reload,
    queryKey,
  } = useServerTable<Student>({
    endpoint: "/admin/users",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    params: { role: "student", search },
    extractRows: (data) => data.users ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  useEffect(() => {
    api
      .get("/admin/users/stats?role=student")
      .then((res) => setStats(res.data.data ?? null))
      .catch(() => setStats(null))
  }, [studentsTotal])

  // Búsqueda unificada: además de filtrar estudiantes en el servidor, consulta reservas
  // por email (SeedbedPendingMember) sin cuenta todavía cuando la búsqueda parece un email/nombre.
  useEffect(() => {
    const q = search.trim()
    if (q.length < 3) {
      setPendingResults([])
      return
    }
    const timeout = setTimeout(() => {
      api
        .get(`/academy/seedbeds/admin/pending-members?email=${encodeURIComponent(q.toLowerCase())}`)
        .then((res) => setPendingResults((res.data.data ?? []).filter((m: PendingMember) => !m.reconciledUserId)))
        .catch(() => setPendingResults([]))
    }, 300)
    return () => clearTimeout(timeout)
  }, [search])

  function openPendingDetail(member: PendingMember) {
    setSelectedPending(member)
  }

  function closePendingDetail() {
    setSelectedPending(null)
  }

  useEffect(() => {
    if (!selectedPending) return
    closeDrawerButtonRef.current?.focus()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePendingDetail()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [selectedPending])

  async function handleReconcile(memberId: string, userId: string) {
    await api.post(`/academy/seedbeds/admin/pending-members/${memberId}/reconcile`, { user_id: userId })
    setPendingResults((prev) => prev.filter((m) => m.id !== memberId))
    if (selectedPending?.id === memberId) closePendingDetail()
    await reload()
  }

  // Bloquear/activar aplica de inmediato a la caché de la página actual y
  // ofrece deshacer, igual que StaffUsersPage.
  function handleToggleActive(student: Student) {
    const nextActive = !student.isActive
    void (async () => {
      const previous = await applyOptimistic<StudentsPayload>(qc, queryKey, (prev) =>
        prev ? { ...prev, users: prev.users.map((u) => (u.id === student.id ? { ...u, isActive: nextActive } : u)) } : prev,
      )
      showUndoToast({
        message: `${student.name} ${nextActive ? "activado" : "bloqueado"}`,
        onUndo: () => rollbackOptimistic(qc, queryKey, previous),
        onCommit: async () => {
          try {
            await studentsAdmin.updateStatus(student.id, nextActive)
            reload()
          } catch {
            rollbackOptimistic(qc, queryKey, previous)
            toastError("No se pudo guardar el cambio.")
          }
        },
      })
    })()
  }

  async function handleResetPassword(student: Student) {
    try {
      const result = await studentsAdmin.resetPassword(student.id)
      setReveal({ label: `Nueva contraseña temporal para ${student.name}`, value: result.tempPassword })
    } catch (err) {
      toastError(err)
    }
  }

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAllVisible() {
    setSelectedIds(new Set(students.map((s) => s.id)))
  }

  function clearSelection() {
    setSelectedIds(new Set())
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return
    setDeleting(true)
    try {
      const result = await studentsAdmin.bulkDelete(Array.from(selectedIds))
      if (result.deleted.length > 0) {
        toastSuccess(`${result.deleted.length} estudiante${result.deleted.length === 1 ? "" : "s"} eliminado${result.deleted.length === 1 ? "" : "s"}.`)
      }
      if (result.skipped.length > 0) {
        toastError(`${result.skipped.length} no se eliminaron (tienen registros asociados u otra razón).`)
      }
      clearSelection()
      reload()
    } catch (err) {
      toastError(err)
    } finally {
      setDeleting(false)
      setConfirmingBulkDelete(false)
    }
  }

  const columns: DataTableColumn<Student>[] = [
    ...(isSuperAdmin
      ? [
          {
            key: "select",
            label: "",
            headerAriaLabel: "Seleccionar",
            render: (student: Student) => (
              <label className="flex min-h-11 min-w-11 cursor-pointer items-center justify-center">
                <span className="sr-only">Seleccionar a {student.name}</span>
                <input
                  type="checkbox"
                  checked={selectedIds.has(student.id)}
                  onChange={() => toggleSelected(student.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                />
              </label>
            ),
          } satisfies DataTableColumn<Student>,
        ]
      : []),
    {
      key: "name",
      label: "Alumno",
      sortable: true,
      render: (student) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{student.name}</p>
          <p className="text-xs text-gray-500">{student.email}</p>
        </>
      ),
    },
    {
      key: "progress",
      label: "Perfil",
      render: (student) => {
        if (!student.studentProfile) {
          return <span className="text-xs text-gray-500 dark:text-gray-400 italic">sin perfil</span>
        }
        const percent = completionPercent(student.studentProfile)
        const tone = completionTone(percent)
        return <span className={`text-xs font-semibold ${COMPLETION_TONE_CLASS[tone]}`}>{percent}%</span>
      },
    },
    {
      key: "isActive",
      label: "Estado",
      render: (student) => (
        <>
          <Badge color={student.isActive ? "success" : "light"} size="sm">
            {student.isActive ? "Activo" : "Bloqueado"}
          </Badge>
          {student.mustChangePassword ? (
            <span className="ml-1.5 text-xs text-warning-600">Pendiente 1er cambio</span>
          ) : null}
        </>
      ),
    },
    {
      key: "createdAt",
      label: "Registrado",
      sortable: true,
      render: (student) => (
        <span className="text-xs text-gray-500">
          {new Date(student.createdAt).toLocaleDateString("es-CO")}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (student) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              { label: "Ver perfil", icon: <EyeIcon />, onClick: () => navigate(`/instructor/students/${student.id}/perfil`) },
              { label: "Inscribir en semillero", icon: <SproutIcon />, onClick: () => navigate(`/instructor/students/enroll?studentId=${student.id}`), requires: "academy:students:edit" },
              { label: "Restablecer contraseña", icon: <LockIcon />, onClick: () => handleResetPassword(student), requires: "academy:students:edit" },
              {
                label: student.isActive ? "Bloquear" : "Activar",
                icon: student.isActive ? <CloseIcon /> : <CheckCircleIcon />,
                onClick: () => handleToggleActive(student),
                variant: student.isActive ? "danger" : "default",
                requires: "academy:students:edit",
              },
            ]}
          />
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Docentes", "Estudiantes"]}
      title="Estudiantes"
      subtitle="Seguimiento de progreso de todos los alumnos"
      tabs={[
        { label: "Panel", to: "/instructor/hub" },
        { label: "Entregas", to: "/instructor/submissions" },
        { label: "Exámenes IA", to: "/instructor/section-quizzes" },
        { label: "Estudiantes", to: "/instructor/students", active: true },
        { label: "Grupos", to: "/instructor/groups" },
      ]}
      metrics={[
        { label: "Total estudiantes", value: String(studentsTotal), hint: "Matriculados en la academia" },
        {
          label: "Activos",
          value: stats ? String(stats.active) : "—",
          hint: stats ? `${stats.blocked} bloqueados` : undefined,
        },
        {
          label: "Nuevos (30 días)",
          value: stats ? String(stats.newLast30Days) : "—",
          hint: "Registrados en el último mes",
        },
        {
          label: "Género",
          value: stats ? `${stats.gender.male}H / ${stats.gender.female}M` : "—",
          hint: stats
            ? `${stats.gender.nonBinary} no binario · ${stats.gender.undeclared} sin declarar`
            : undefined,
        },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar por nombre o email (incluye reservas sin cuenta)...",
        },
        filters: [
          {
            type: "checkbox",
            key: "at-risk",
            label: "Solo en riesgo",
            checked: atRiskOnly,
            onChange: setAtRiskOnly,
          },
        ],
        onRefresh: reload,
        refreshing: loading,
        onImport: () => navigate("/instructor/students/import"),
        onCreate: () => navigate("/instructor/students/new"),
        createLabel: "+ Crear estudiante",
        requires: "academy:students:edit",
        extra: <TourButton steps={supportAccountsTourSteps} />,
      }}
    >
      {error && (
        <p role="alert" aria-live="assertive" className="text-error-600 dark:text-error-400 mb-4">
          {error}
        </p>
      )}

      {reveal ? (
        <div className="mb-4">
          <TempPasswordReveal label={reveal.label} value={reveal.value} onDismiss={() => setReveal(null)} />
        </div>
      ) : null}

      {atRiskOnly && (
        <div className="mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
          El filtro "en riesgo" requiere un endpoint de progreso individual aún no disponible. Se muestran todos los estudiantes.
        </div>
      )}

      {pendingResults.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 dark:border-amber-800 p-3 space-y-2">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
            Reservas pendientes de cuenta que coinciden con la búsqueda
          </p>
          {pendingResults.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => openPendingDetail(m)}
              className="flex min-h-11 w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-amber-50 dark:hover:bg-amber-900/10"
            >
              <span>
                <span className="font-medium text-gray-900 dark:text-white">{m.name}</span>{" "}
                <span className="text-xs text-gray-500">{m.email}</span>
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{m.seedbed?.name}</span>
            </button>
          ))}
        </div>
      )}

      {isSuperAdmin && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm dark:border-gray-800 dark:bg-white/[0.02]">
          <span role="status" aria-live="polite" className="text-gray-500 dark:text-gray-400">
            {deleting
              ? "Eliminando estudiantes..."
              : selectedIds.size > 0
                ? `${selectedIds.size} seleccionado${selectedIds.size === 1 ? "" : "s"}`
                : "Borrado masivo (solo Administrador)"}
          </span>
          <button type="button" onClick={selectAllVisible} className="min-h-11 text-xs font-medium text-brand-600 hover:underline">
            Seleccionar visibles
          </button>
          {selectedIds.size > 0 && (
            <>
              <button type="button" onClick={clearSelection} className="min-h-11 text-xs font-medium text-gray-500 hover:underline">
                Limpiar selección
              </button>
              <button
                type="button"
                onClick={() => setConfirmingBulkDelete(true)}
                disabled={deleting}
                className="ml-auto min-h-11 rounded-lg bg-error-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-error-700 disabled:opacity-60"
              >
                {deleting ? "Eliminando..." : `Eliminar seleccionados (${selectedIds.size})`}
              </button>
            </>
          )}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={students}
        rowKey={(student) => student.id}
        loading={loading}
        emptyMessage="No se encontraron estudiantes."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={studentsTotal}
        onPageChange={setPage}
      />

      {/* Drawer: solo para reservas pendientes (sin cuenta, sin perfil propio) */}
      {selectedPending && (
        <div className="fixed inset-0 z-40" aria-hidden="true" onClick={closePendingDetail} />
      )}
      <div
        role="dialog"
        aria-modal={selectedPending ? true : undefined}
        aria-labelledby={selectedPending ? "pending-detail-heading" : undefined}
        className={`fixed inset-y-0 right-0 z-50 w-[480px] bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700 transform transition-transform duration-300 overflow-y-auto ${
          selectedPending ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {selectedPending && (
          <div className="p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 id="pending-detail-heading" className="text-xl font-bold text-gray-900 dark:text-white">{selectedPending.name}</h2>
                <p className="text-sm text-gray-500">{selectedPending.email}</p>
                <span className="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                  Pendiente de cuenta
                </span>
              </div>
              <button
                type="button"
                ref={closeDrawerButtonRef}
                onClick={closePendingDetail}
                aria-label="Cerrar detalle"
                className="flex min-h-11 min-w-11 items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none"
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Semilleros</h3>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/instructor/students/enroll?pendingId=${selectedPending.id}&name=${encodeURIComponent(selectedPending.name)}&email=${encodeURIComponent(selectedPending.email)}`
                    )
                  }
                  className="text-xs font-medium text-blue-600 hover:underline"
                >
                  Inscribir en semillero
                </button>
              </div>

              <PendingMemberReconcileRow
                member={selectedPending}
                candidates={students}
                onReconcile={handleReconcile}
                showSeedbed
              />
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmingBulkDelete}
        title={`¿Eliminar permanentemente ${selectedIds.size} estudiante${selectedIds.size === 1 ? "" : "s"}?`}
        message="Esta acción no se puede deshacer. Los que tengan inscripciones, pagos, certificados o equipo CCCD no se eliminarán."
        confirmLabel="Eliminar"
        confirming={deleting}
        onConfirm={() => void handleBulkDelete()}
        onCancel={() => setConfirmingBulkDelete(false)}
      />
    </ModuleLayout>
  )
}
